import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { MAXIMUM_PASSENGERS } from '../../../common/Passengers/constants';
import { EPassengerCategory, IDiscountCard } from '../../../common/types';
import { IPassenger } from '../../../contexts/Search/types';
import { getPassengersCountPerCategory } from '../../../contexts/Search/utils';
import windowSize from '../../../helpers/windowSize';
import { useDelayedAction } from '../../../hooks/useDelayedAction';
import { ToastType, useToasts } from '../../../hooks/useToasts';
import { EditIcon } from '../../../images/icons/Edit';
import { MinusIcon } from '../../../images/icons/minus';
import { PlusIcon } from '../../../images/icons/plus';
import { colors } from '../../../styles/constants';
import Button from '../../Forms/Button';
import { Select } from '../../Forms/Select';
import {
  PanelScrollableWrapper,
  PanelWrapper,
  PanelWrapperStyle,
} from '../../Funnel/Panel/common';
import { MainPanel } from '../../Funnel/Panel/MainPanel';
import { Icon18, Icon24 } from '../../Icon';
import {
  OVERLAY_ANIMATION_DURATION,
  TSB_ADD_PASSENGER_BUTTON_TEST_ID,
  TSB_PASSENGERS_PANEL_TEST_ID,
  TSB_REMOVE_PASSENGER_BUTTON_TEST_ID,
  TSB_UPDATE_PASSENGER_AGE_SELECTOR_TEST_ID,
  TSB_UPDATE_PASSENGER_DISCOUNT_CARD_BUTTON_TEST_ID,
  YOUNG_PASSENGER_MAX_AGE,
  YOUNG_PASSENGER_MIN_AGE,
} from '../constants';
import messages from '../messages';
import { PanelCTAStyle } from './common';
import DiscountCardsPanel from './DiscountCardsPanel';

const CategoriesContainer = styled.form`
  ${tw`min-h-full flex flex-col`}
  gap:var(--size-medium);
`;

const PassengersCount = styled(PanelWrapper)<{ hasPassenger: boolean }>`
  ${tw`text-p-medium-m leading-p-medium-m text-text `}

  ${({ hasPassenger }) => !hasPassenger && tw`text-secondary-light-100`}
`;

const ScrollableWrapper = styled(PanelScrollableWrapper)`
  ${tw`h-full`}
`;

const Wrapper = styled.div`
  ${tw`h-full pb-medium2 relative overflow-hidden`}
`;

const ToasQueueStyle = css`
  ${PanelWrapperStyle}
  ${tw`mb-medium`}
`;

export const PassengersRecap: React.FC<{
  passengers: IPassenger[];
  withAgeRange?: boolean;
}> = ({ passengers, withAgeRange }) => {
  const translations = [];

  const { adults, seniors, youths } = getPassengersCountPerCategory(passengers);

  if (adults > 0) {
    translations.push(
      withAgeRange ? (
        <FormattedMessage
          {...messages[
            `passengersPanelCategory-${EPassengerCategory.ADULT}-age`
          ]}
          values={{
            category: (
              <FormattedMessage
                {...messages[`passengers-${EPassengerCategory.ADULT}-count`]}
                values={{
                  count: adults,
                }}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          {...messages[`passengers-${EPassengerCategory.ADULT}-count`]}
          values={{
            count: adults,
          }}
        />
      ),
    );
  }

  if (youths > 0) {
    translations.push(
      withAgeRange ? (
        <FormattedMessage
          {...messages[
            `passengersPanelCategory-${EPassengerCategory.YOUNG}-age`
          ]}
          values={{
            category: (
              <FormattedMessage
                {...messages[`passengers-${EPassengerCategory.YOUNG}-count`]}
                values={{
                  count: youths,
                }}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          {...messages[`passengers-${EPassengerCategory.YOUNG}-count`]}
          values={{
            count: youths,
          }}
        />
      ),
    );
  }

  if (seniors > 0) {
    translations.push(
      withAgeRange ? (
        <FormattedMessage
          {...messages[
            `passengersPanelCategory-${EPassengerCategory.SENIOR}-age`
          ]}
          values={{
            category: (
              <FormattedMessage
                {...messages[`passengers-${EPassengerCategory.SENIOR}-count`]}
                values={{
                  count: seniors,
                }}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          {...messages[`passengers-${EPassengerCategory.SENIOR}-count`]}
          values={{
            count: seniors,
          }}
        />
      ),
    );
  }

  return translations.reduce(
    (previous, current, index) => (
      <React.Fragment>
        {previous}
        {index > 0 && <span>, </span>}
        {current}
      </React.Fragment>
    ),
    null,
  );
};

const TOAST_DURATION = 3000;
const ADD_PASSENGER_TOAST_DURATION = 6000;

interface PassengersPanelProps {
  passengers: IPassenger[];
  onClose: () => void;
  onAddPassenger: (category: EPassengerCategory) => void;
  onRemovePassenger: (id: number) => void;
  onUpdatePassenger: (updatedPassenger: IPassenger) => void;
  getDiscountCards: () => Promise<IDiscountCard[]>;
  setPassengers: (passengers: IPassenger[]) => void;
  closePanelButtonRef: React.MutableRefObject<HTMLButtonElement>;
  formRef: React.MutableRefObject<HTMLFormElement>;
}

const PassengersPanel: React.FC<PassengersPanelProps> = ({
  closePanelButtonRef,
  formRef,
  getDiscountCards,
  onAddPassenger,
  onClose,
  onRemovePassenger,
  onUpdatePassenger,
  passengers,
  setPassengers,
}) => {
  const [discountCardsPanelPassenger, setDiscountCardsPanelPassenger] =
    useState<IPassenger>(null);

  const [initialPassengers] = useState<IPassenger[]>(passengers);

  const toastsQueue = useToasts(ToasQueueStyle);

  useEffect(() => {
    if (passengers.length === 0) {
      toastsQueue.add({
        type: ToastType.ERROR,
        message: <FormattedMessage {...messages.selectAtLeastOnePassenger} />,
        duration: TOAST_DURATION,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengers.length]);

  function onUpdatePassengerAge(passenger: IPassenger, age: number) {
    onUpdatePassenger({
      ...passenger,
      age,
    });
  }

  function onUpdatePassengerDicountCard(
    passenger: IPassenger,
    discountCard: IDiscountCard,
  ) {
    onUpdatePassenger({
      ...passenger,
      discountCards: discountCard ? [discountCard] : [],
    });

    setTimeout(() => {
      toastsQueue.add({
        type: ToastType.SUCCESS,
        message: discountCard ? (
          <div>
            <FormattedMessage {...messages.discountCardSuccessfullyAdded} />
            <br />
            <FormattedMessage
              {...messages.discountCardWillBeTakenIntoAccountLater}
            />
          </div>
        ) : (
          <FormattedMessage {...messages.discountCardSuccessfullyRemoved} />
        ),
        duration: TOAST_DURATION,
      });
    }, OVERLAY_ANIMATION_DURATION);
  }

  function findAllPassengers(category: EPassengerCategory): IPassenger[] {
    return passengers.filter((passenger) => passenger.category === category);
  }

  function addPassenger(category: EPassengerCategory): void {
    if (passengers.length === MAXIMUM_PASSENGERS - 1) {
      toastsQueue.add({
        type: ToastType.WARNING,
        message: (
          <FormattedMessage {...messages.passengerLimitHasBeenReached} />
        ),
        duration: ADD_PASSENGER_TOAST_DURATION,
      });
    }

    onAddPassenger(category);
  }

  const canAddPassengers = passengers.length < MAXIMUM_PASSENGERS;

  return (
    <MainPanel
      CTAButtonContent={<FormattedMessage {...messages.validate} />}
      CTAContainerStyle={PanelCTAStyle}
      closeButtonRef={closePanelButtonRef}
      isCTAButtonDisabled={passengers.length === 0}
      onCTAButtonClick={() => {
        if (formRef.current.checkValidity()) {
          closePanelButtonRef.current.click();
        } else {
          formRef.current.reportValidity();
        }
      }}
      onClose={(event) => {
        // event.isTrusted is true when the event was generated by a user action, and false when the event was created or modified by a script or dispatched
        if (windowSize.smallSize() && event.isTrusted) {
          setPassengers(initialPassengers);
        }
        onClose();
      }}
      testid={TSB_PASSENGERS_PANEL_TEST_ID}
      title={<FormattedMessage {...messages.passengersPaneOverlayTitle} />}
    >
      {discountCardsPanelPassenger !== null && (
        <DiscountCardsPanel
          getDiscountCards={getDiscountCards}
          onClose={() => setDiscountCardsPanelPassenger(null)}
          onDiscountCardChange={(discountCard) =>
            onUpdatePassengerDicountCard(
              discountCardsPanelPassenger,
              discountCard,
            )
          }
          selectedDiscountCard={
            discountCardsPanelPassenger.discountCards.length > 0
              ? (discountCardsPanelPassenger.discountCards[0] as IDiscountCard)
              : null
          }
        />
      )}

      <PassengersCount hasPassenger={passengers.length > 0}>
        {passengers.length > 0 ? (
          <PassengersRecap passengers={passengers} />
        ) : (
          <FormattedMessage
            {...messages.passengersCount}
            values={{
              count: passengers.length,
            }}
          />
        )}
      </PassengersCount>
      <Wrapper>
        <ScrollableWrapper>
          <CategoriesContainer ref={formRef}>
            <PassengersCategory
              canAddPassengers={canAddPassengers}
              category={EPassengerCategory.ADULT}
              onAddPassenger={addPassenger}
              onRemovePassenger={onRemovePassenger}
              onUpdatePassengerAge={onUpdatePassengerAge}
              openDiscountCardsPanel={setDiscountCardsPanelPassenger}
              passengers={findAllPassengers(EPassengerCategory.ADULT)}
            />

            <PassengersCategory
              canAddPassengers={canAddPassengers}
              category={EPassengerCategory.YOUNG}
              maxAge={YOUNG_PASSENGER_MAX_AGE}
              minAge={YOUNG_PASSENGER_MIN_AGE}
              onAddPassenger={addPassenger}
              onRemovePassenger={onRemovePassenger}
              onUpdatePassengerAge={onUpdatePassengerAge}
              openDiscountCardsPanel={setDiscountCardsPanelPassenger}
              passengers={findAllPassengers(EPassengerCategory.YOUNG)}
              withAge
            />
            <PassengersCategory
              canAddPassengers={canAddPassengers}
              category={EPassengerCategory.SENIOR}
              onAddPassenger={addPassenger}
              onRemovePassenger={onRemovePassenger}
              onUpdatePassengerAge={onUpdatePassengerAge}
              openDiscountCardsPanel={setDiscountCardsPanelPassenger}
              passengers={findAllPassengers(EPassengerCategory.SENIOR)}
            />
          </CategoriesContainer>
        </ScrollableWrapper>
        {toastsQueue.ToastsComponent}
      </Wrapper>
    </MainPanel>
  );
};

const PassengersCategoryContainer = styled.div`
  ${tw`rounded-15 border-solid border-grey-300 overflow-hidden`}
`;

const PassengersCategoryWrapper = styled.div`
  ${tw`p-medium`}
`;

const PassengersCategoryHeader = styled(PassengersCategoryWrapper)`
  ${tw`bg-white flex items-center justify-between cursor-pointer`}
  gap:var(--size-small);
`;

const PassengersCategoryTitle = styled.span`
  ${tw`text-p-medium-m leading-p-medium-m font-bold
    md:text-p-small md:leading-p-small
`}
`;

const IconStyle = css`
  ${tw`rounded-full border-solid border-secondary-400 flex items-center justify-center`}
  ${Icon24}
`;

const IconButton = styled.button`
  ${({ disabled }) =>
    disabled &&
    css`
      > * {
        ${tw`border-grey-300`}
      }
    `}
  ${tw``}
`;

interface PassengersCategoryProps {
  withAge?: boolean;
  minAge?: number;
  maxAge?: number;

  category: EPassengerCategory;
  onAddPassenger: (category: EPassengerCategory) => void;
  onRemovePassenger: (id: number) => void;
  onUpdatePassengerAge: (passenger: IPassenger, age: number) => void;
  openDiscountCardsPanel: (passenger: IPassenger) => void;
  canAddPassengers: boolean;

  passengers: IPassenger[];
}

const PassengersCategory: React.FC<PassengersCategoryProps> = ({
  canAddPassengers,
  category,
  maxAge,
  minAge,
  onAddPassenger,
  onRemovePassenger,
  onUpdatePassengerAge,
  openDiscountCardsPanel,
  passengers,
  withAge,
}) => {
  const containerRef = useRef(null);
  const delayed = useDelayedAction();

  return (
    <PassengersCategoryContainer>
      <PassengersCategoryHeader
        onClick={() => {
          if (!canAddPassengers) {
            return;
          }

          onAddPassenger(category);

          delayed.delay(() => {
            containerRef.current.lastChild.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            });
          }, 150);
        }}
      >
        <PassengersCategoryTitle>
          <FormattedMessage
            {...messages[`passengersPanelCategory-${category}-age`]}
            values={{
              category: (
                <FormattedMessage
                  {...messages[`passengersPanelCategory-${category}`]}
                />
              ),
            }}
          />
        </PassengersCategoryTitle>

        <IconButton
          data-testid={TSB_ADD_PASSENGER_BUTTON_TEST_ID[category]}
          disabled={!canAddPassengers}
          type="button"
        >
          <PlusIcon
            color={
              !canAddPassengers ? colors['grey-200'] : colors['secondary-400']
            }
            customStyle={IconStyle}
          />
        </IconButton>
      </PassengersCategoryHeader>

      <div ref={containerRef}>
        {passengers.map((passenger, index) => (
          <Passenger
            key={index}
            category={category}
            index={index + 1}
            maxAge={maxAge}
            minAge={minAge}
            onRemove={onRemovePassenger}
            onUpdatePassengerAge={onUpdatePassengerAge}
            openDiscountCardsPanel={openDiscountCardsPanel}
            passenger={passenger}
            withAge={withAge}
          />
        ))}
      </div>
    </PassengersCategoryContainer>
  );
};

const PassengerContainer = styled(PassengersCategoryWrapper)`
  border-top: 1px solid;
  ${tw`bg-grey-100 border-grey-300 flex flex-col`}
  gap:var(--size-small);
`;

const PassengerContentWrapper = styled.div`
  ${tw`flex justify-between items-center`}
`;

const PassengerContent = styled.div`
  ${tw`flex items-center`}
  gap:var(--size-small);

  white-space: nowrap;
  overflow: hidden;
`;

const PassengerCategory = styled.span`
  ${tw`text-p-medium-m leading-p-medium-m`}
`;

const AddDiscountCardIconButtonStyle = css`
  ${Icon18}
`;

const AddDiscountCardButtonStyle = css`
  ${tw`px-0`}
  white-space: nowrap;
  overflow: hidden;
  span {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

interface PassengerProps {
  withAge?: boolean;
  minAge?: number;
  maxAge?: number;
  index: number;
  category: EPassengerCategory;

  passenger: IPassenger;
  onRemove: (id: number) => void;
  onUpdatePassengerAge: (passenger: IPassenger, age: number) => void;
  openDiscountCardsPanel: (passenger: IPassenger) => void;
}

const Passenger: React.FC<PassengerProps> = ({
  category,
  index,
  maxAge,
  minAge,
  onRemove,
  onUpdatePassengerAge,
  openDiscountCardsPanel,
  passenger,
  withAge,
}) => (
  <PassengerContainer>
    <PassengerContentWrapper>
      <PassengerContent>
        <PassengerCategory>
          <FormattedMessage
            {...messages[`passengersPanelCategory-${category}`]}
          />{' '}
          {index}
        </PassengerCategory>

        <Button
          css={AddDiscountCardButtonStyle}
          data-testid={
            TSB_UPDATE_PASSENGER_DISCOUNT_CARD_BUTTON_TEST_ID[category]
          }
          onClick={() => openDiscountCardsPanel(passenger)}
          rightIcon={
            passenger.discountCards.length > 0 ? (
              <EditIcon
                color={colors['primary-400']}
                customStyle={AddDiscountCardIconButtonStyle}
              />
            ) : (
              <PlusIcon
                color={colors['primary-400']}
                customStyle={AddDiscountCardIconButtonStyle}
              />
            )
          }
          size="MEDIUM"
          type="button"
          variant="TERCIARY"
        >
          <span>
            {passenger.discountCards.length > 0 ? (
              passenger.discountCards[0].label
            ) : (
              <FormattedMessage {...messages.passengersPanelDiscountCard} />
            )}
          </span>
        </Button>
      </PassengerContent>

      <button
        data-testid={TSB_REMOVE_PASSENGER_BUTTON_TEST_ID[category]}
        onClick={() => onRemove(passenger.id)}
        type="button"
      >
        <MinusIcon color={colors['secondary-400']} customStyle={IconStyle} />
      </button>
    </PassengerContentWrapper>

    {withAge && (
      <PassengerAge
        age={passenger.age}
        max={maxAge}
        min={minAge}
        onAgeChange={(age: number) => {
          onUpdatePassengerAge(passenger, age);
        }}
      />
    )}
  </PassengerContainer>
);

interface PassengerAgeProps {
  min: number;
  max: number;

  age: number | undefined;
  onAgeChange: (age: number) => void;
}

const PassengerAge: React.FC<PassengerAgeProps> = ({
  age,
  max,
  min,
  onAgeChange,
}) => {
  function generateAges() {
    const ages: number[] = [];

    for (let i = max; i >= min; i--) {
      ages.push(i);
    }

    return ages;
  }

  return (
    <Select
      customStyle={css`
        height: max-content;
      `}
      data-testid={TSB_UPDATE_PASSENGER_AGE_SELECTOR_TEST_ID}
      onChange={(e) => {
        const value = Number(e.target.value);
        onAgeChange(isNaN(value) ? undefined : value);
      }}
      required
      value={age >= 0 ? age : ''}
    >
      <FormattedMessage {...messages.passengersPanelAge}>
        {(msg) => (
          <option disabled hidden value="">
            {msg}
          </option>
        )}
      </FormattedMessage>

      {generateAges().map((age, index) => (
        <FormattedMessage
          {...messages.passengersPanelPassengerAge}
          key={index}
          values={{
            age,
          }}
        >
          {(message) => <option value={age}>{message}</option>}
        </FormattedMessage>
      ))}
    </Select>
  );
};

export default PassengersPanel;
